import type {Dispatch, ThunkAction} from 'src/reducers';
import type {
  UploadedFile,
  UploadResponse,
  UploadError,
  SecureUploadFile,
  BrandInfo,
  SecureUploadFilesApiResponse,
} from 'src/types/messaging-secure-upload';
import * as reduxApi from 'src/utils/redux-api-v2';
import {key, fetching} from 'src/utils/redux';
import {thunkify as flow} from 'src/utils/thunks';
import {camel} from 'src/utils';


export const RECEIVE_UPLOADED_FILES =
  'messaging-secure-upload/receiveUploadedFiles';
export const RECEIVE_UPLOADED_FILES_ERROR =
  'messaging-secure-upload/receiveUploadedFilesError';
export const RECEIVE_UPLOAD_RESPONSE =
  'messaging-secure-upload/receiveUploadResponse';
export const RECEIVE_UPLOAD_ERROR =
  'messaging-secure-upload/receiveUploadError';
export const RECEIVE_THREAD_UPLOADS =
  'messaging-secure-upload/receiveThreadUploads';
export const RECEIVE_THREAD_UPLOADS_ERROR =
  'messaging-secure-upload/receiveThreadUploadsError';
export const RECEIVE_MORE_THREAD_UPLOADS =
  'messaging-secure-upload/receiveMoreThreadUploads';
export const CLEAR_THREAD_UPLOADS =
  'messaging-secure-upload/clearThreadUploads';

type ReceiveUploadedFilesAction = {
  type: typeof RECEIVE_UPLOADED_FILES;
  payload: {
    files: SecureUploadFile[];
    brandInfo: BrandInfo;
  };
};

type ReceiveUploadedFilesErrorAction = {
  type: typeof RECEIVE_UPLOADED_FILES_ERROR;
  payload: UploadError;
};

type ReceiveUploadResponseAction = {
  type: typeof RECEIVE_UPLOAD_RESPONSE;
  payload: UploadResponse;
};

type ReceiveUploadErrorAction = {
  type: typeof RECEIVE_UPLOAD_ERROR;
  payload: UploadError;
};

type ReceiveThreadUploadsAction = {
  type: typeof RECEIVE_THREAD_UPLOADS;
  payload: UploadedFile[];
};

type ReceiveThreadUploadsErrorAction = {
  type: typeof RECEIVE_THREAD_UPLOADS_ERROR;
  payload: UploadError;
};

type ReceiveMoreThreadUploadsAction = {
  type: typeof RECEIVE_MORE_THREAD_UPLOADS;
  payload: UploadedFile[];
};

type ClearThreadUploadsAction = {
  type: typeof CLEAR_THREAD_UPLOADS;
};

export type MessagingSecureUploadAction =
  | ReceiveUploadedFilesAction
  | ReceiveUploadedFilesErrorAction
  | ReceiveUploadResponseAction
  | ReceiveUploadErrorAction
  | ReceiveThreadUploadsAction
  | ReceiveThreadUploadsErrorAction
  | ReceiveMoreThreadUploadsAction
  | ClearThreadUploadsAction;

const receiveUploadedFiles = (
  files: SecureUploadFile[],
  brandInfo: BrandInfo,
): ReceiveUploadedFilesAction => ({
  type: RECEIVE_UPLOADED_FILES,
  payload: {files, brandInfo},
});

const receiveUploadedFilesError = (
  error: UploadError,
): ReceiveUploadedFilesErrorAction => ({
  type: RECEIVE_UPLOADED_FILES_ERROR,
  payload: error,
});

const receiveUploadResponse = (
  response: UploadResponse,
): ReceiveUploadResponseAction => ({
  type: RECEIVE_UPLOAD_RESPONSE,
  payload: response,
});

const receiveUploadError = (error: UploadError): ReceiveUploadErrorAction => ({
  type: RECEIVE_UPLOAD_ERROR,
  payload: error,
});

const receiveThreadUploads = (
  files: UploadedFile[],
): ReceiveThreadUploadsAction => ({
  type: RECEIVE_THREAD_UPLOADS,
  payload: files,
});

const receiveThreadUploadsError = (
  error: UploadError,
): ReceiveThreadUploadsErrorAction => ({
  type: RECEIVE_THREAD_UPLOADS_ERROR,
  payload: error,
});

const receiveMoreThreadUploads = (
  files: UploadedFile[],
): ReceiveMoreThreadUploadsAction => ({
  type: RECEIVE_MORE_THREAD_UPLOADS,
  payload: files,
});

const clearThreadUploads = (): ClearThreadUploadsAction => ({
  type: CLEAR_THREAD_UPLOADS,
});

export const getUploadedFiles: (token: string) => ThunkAction<unknown> = flow(
  key(() => 'messaging-secure-upload/getUploadedFiles'),
  fetching(),
)((token: string) => (dispatch: Dispatch) => {
  return dispatch(reduxApi.get(`messages_v2/secure-upload/${token}/files`))
    .then((response: SecureUploadFilesApiResponse) => {
      const uploadedFilesResponse = camel(response);
      const {brandDetails, uploads} = uploadedFilesResponse;

      const brandInfo = (brandDetails || null) as BrandInfo;

      const successfulFiles: SecureUploadFile[] = Array.isArray(uploads)
        ? uploads.map((file) => file as SecureUploadFile)
        : [];
      dispatch(receiveUploadedFiles(successfulFiles, brandInfo));
    })
    .catch((error) => {
      dispatch(
        receiveUploadedFilesError({
          error: 'API_ERROR',
          message:
            error.responseBody?.message || 'Failed to load uploaded files',
          code: error?.response?.status?.toString(),
        }),
      );
    });
});

export const getThreadUploads: (
  threadId: string,
  offset?: number,
  limit?: number,
) => ThunkAction<unknown> = flow(
  key(() => 'messaging-secure-upload/getThreadUploads'),
  fetching(),
)(
  (threadId: string, offset: number = 0, limit: number = 10) =>
    (dispatch: Dispatch) => {
      return dispatch(
        reduxApi.get(`messages_v2/thread/${threadId}/uploads`, {
          offset,
          limit,
        }),
      )
        .then((files) => {
          if (offset === 0) {
            dispatch(receiveThreadUploads(files));
          } else {
            dispatch(receiveMoreThreadUploads(files));
          }
        })
        .catch((error) => {
          dispatch(
            receiveThreadUploadsError({
              error: 'API_ERROR',
              message:
                error.responseBody?.message || 'Failed to load thread uploads',
              code: error?.response?.status?.toString(),
            }),
          );
        });
    },
);

export const uploadFile: (token: string, file: File) => ThunkAction<unknown> =
  flow(
    key(() => 'messaging-secure-upload/uploadFile'),
    fetching(),
  )((token: string, file: File) => (dispatch: Dispatch) => {
    const formData = new FormData();
    formData.append('file', file);
    return dispatch(
      reduxApi.post(`/messages_v2/secure-upload/${token}/files`, formData),
    )
      .then((data) => {
        dispatch(receiveUploadResponse(data));
      })
      .catch((error) => {
        dispatch(
          receiveUploadError({
            error: 'API_ERROR',
            message: error.responseBody?.message || 'Failed to upload file',
            code: error?.response?.status?.toString(),
          }),
        );
      });
  });

export const clearThreadUploadsAction = (): ClearThreadUploadsAction =>
  clearThreadUploads();
